import { z } from 'zod';
import { phoneSchema } from './phone.js';
/**
 * WhatsApp phone verification (customers.md section 10). The same shapes
 * serve the integration API (other systems) and the Customers web app.
 */
export const phoneVerificationPurposeSchema = z.enum(['CREATE', 'CHANGE_PHONE']);
export const phoneVerificationStatusSchema = z.enum([
    'PENDING',
    'VERIFIED',
    'CONSUMED',
    'EXPIRED',
    'LOCKED',
]);
const employeeIdSchema = z.string().trim().min(1).max(100);
const employeeNameSchema = z.string().trim().min(1).max(200);
const customerIdSchema = z.string().trim().min(1).max(64);
const ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
const EASTERN_ARABIC_INDIC = '۰۱۲۳۴۵۶۷۸۹';
/** Six digits; Arabic-Indic and Eastern Arabic-Indic digits and spaces are accepted. */
export const otpCodeSchema = z
    .string()
    .transform((value) => [...value]
    .map((char) => {
    const arabic = ARABIC_INDIC.indexOf(char);
    if (arabic >= 0)
        return String(arabic);
    const eastern = EASTERN_ARABIC_INDIC.indexOf(char);
    return eastern >= 0 ? String(eastern) : char;
})
    .join('')
    .replace(/\s/g, ''))
    .pipe(z.string().regex(/^\d{6}$/, 'must be six digits'));
/** The binding rule of section 10.4: CHANGE_PHONE needs the customer, CREATE refuses one. */
function refineBinding(value, ctx) {
    if (value.purpose === 'CHANGE_PHONE' && !value.customerId) {
        ctx.addIssue({
            code: 'custom',
            path: ['customerId'],
            message: 'customerId is required for CHANGE_PHONE',
        });
    }
    if (value.purpose === 'CREATE' && value.customerId) {
        ctx.addIssue({
            code: 'custom',
            path: ['customerId'],
            message: 'customerId is not allowed for CREATE',
        });
    }
}
/** POST /integration/phone-verifications. */
export const integrationRequestPhoneVerificationSchema = z
    .object({
    phone: phoneSchema,
    purpose: phoneVerificationPurposeSchema,
    language: z.enum(['ar', 'en']).default('ar'),
    customerId: customerIdSchema.optional(),
    employeeId: employeeIdSchema,
    employeeName: employeeNameSchema.optional(),
})
    .superRefine(refineBinding);
/** POST /integration/phone-verifications/{id}/resend: only the requesting employee may resend. */
export const integrationResendPhoneVerificationSchema = z.object({
    employeeId: employeeIdSchema,
    employeeName: employeeNameSchema.optional(),
});
/** POST /integration/phone-verifications/{id}/verify. */
export const integrationVerifyPhoneVerificationSchema = z.object({
    code: otpCodeSchema,
    employeeId: employeeIdSchema,
    employeeName: employeeNameSchema.optional(),
});
/** POST /integration/phone-verifications/{id}/consume: matched against the binding. */
export const integrationConsumePhoneVerificationSchema = z.object({
    phone: phoneSchema,
    purpose: phoneVerificationPurposeSchema,
    customerId: customerIdSchema.optional(),
    employeeId: employeeIdSchema,
});
/** POST /phone-verifications (the Customers web app; the employee comes from the session). */
export const requestPhoneVerificationSchema = z
    .object({
    phone: phoneSchema,
    purpose: phoneVerificationPurposeSchema,
    language: z.enum(['ar', 'en']).default('ar'),
    customerId: customerIdSchema.optional(),
})
    .superRefine(refineBinding);
/** POST /phone-verifications/{id}/verify (the Customers web app). */
export const verifyPhoneVerificationSchema = z.object({ code: otpCodeSchema });
export const phoneVerificationSentSchema = z.object({
    id: z.string(),
    phone: z.string(),
    expiresAt: z.string(),
    resendAvailableAt: z.string(),
    attemptsLeft: z.number().int(),
});
export const phoneVerificationAlreadyVerifiedSchema = z.object({
    alreadyVerified: z.literal(true),
    customerId: z.string(),
});
export const phoneVerificationRequestResultSchema = z.union([
    phoneVerificationSentSchema,
    phoneVerificationAlreadyVerifiedSchema,
]);
export const phoneVerificationVerifiedSchema = z.object({
    id: z.string(),
    phone: z.string(),
    status: z.literal('VERIFIED'),
    verifiedAt: z.string(),
});
export const phoneVerificationReceiptSchema = z.object({
    receiptId: z.string(),
    phone: z.string(),
    purpose: phoneVerificationPurposeSchema,
    customerId: z.string().nullable(),
    consumedAt: z.string(),
});
export const whatsappDeliveryStatusSchema = z.enum([
    'queued',
    'sent',
    'delivered',
    'read',
    'failed',
]);
export const phoneVerificationViewSchema = z.object({
    id: z.string(),
    phone: z.string(),
    status: phoneVerificationStatusSchema,
    purpose: phoneVerificationPurposeSchema,
    verifiedAt: z.string().nullable(),
    usableUntil: z.string().nullable(),
    deliveryStatus: whatsappDeliveryStatusSchema.nullable(),
});
//# sourceMappingURL=verification.js.map